import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { InputActionMeta } from "react-select";

import { useGetCompanyContactBook } from "api/queries/company/useGetCompanyContactBook";
import useDebounce from "hooks/useDebounce";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import {
  buildContactSelectOptions,
  ContactBookOption,
} from "@RequestFormV2/sections/Schedule/utils/contactBook";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";

const CONTACT_SEARCH_DEBOUNCE_MS = 400;

export function useStopContact(stopIndex: number) {
  const { setValue } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const { location } = useSchedule();
  const [contactSearchQuery, setContactSearchQuery] = useState("");

  const debouncedContactQuery = useDebounce(
    contactSearchQuery.length >= 3 ? contactSearchQuery : "",
    CONTACT_SEARCH_DEBOUNCE_MS,
  );

  const isSearching = contactSearchQuery.length >= 3;
  const searchQuery = isSearching ? debouncedContactQuery : undefined;

  const { data: contactSearchResults = [], isFetching: isLoadingContacts } = useGetCompanyContactBook(
    {
      location: location ?? "",
      ...(searchQuery && { query: searchQuery }),
      stopIndex,
    },
    { enabled: !!location, keepPreviousData: isSearching },
  );

  useEffect(() => {
    setContactSearchQuery("");
  }, [location]);

  const fieldPaths = useMemo(
    () => ({
      stop_contact_name: getStopFieldPath(stopIndex, "stop_contact_name"),
      stop_contact_number: getStopFieldPath(stopIndex, "stop_contact_number"),
      stop_contact_email: getStopFieldPath(stopIndex, "stop_contact_email"),
    }),
    [stopIndex],
  );

  const contactName = useWatch({ name: fieldPaths.stop_contact_name });

  const contactSelectOptions = useMemo(
    () => buildContactSelectOptions({ mapped: contactSearchResults, contactName }),
    [contactSearchResults, contactName],
  );

  const handleSelectContact = useCallback(
    (contact: ContactBookOption) => {
      if (!contact) return;
      setValue(getStopFieldPath(stopIndex, "stop_contact_name"), contact.label);
      // Free-typed synthetic options only have label/value — do not wipe phone/email.
      if (!("phone_number" in contact) && !("email" in contact)) return;
      setValue(getStopFieldPath(stopIndex, "stop_contact_number"), contact.phone_number ?? "");
      setValue(getStopFieldPath(stopIndex, "stop_contact_email"), contact.email ?? "");
    },
    [setValue, stopIndex],
  );

  const handleContactSelect = useCallback(
    (val: string | null) => {
      if (!val) {
        setContactSearchQuery("");
        setValue(fieldPaths.stop_contact_name, "");
        setValue(fieldPaths.stop_contact_number, "");
        setValue(fieldPaths.stop_contact_email, "");
        return;
      }
      const contact = contactSelectOptions.find((c) => c.value === val);
      if (contact) {
        handleSelectContact(contact);
      }
    },
    [contactSelectOptions, handleSelectContact, fieldPaths, setValue],
  );

  const handleContactNameInputChange = useCallback(
    (val: string, meta: InputActionMeta) => {
      if (meta.action === "clear" || (meta.action === "set-value" && !val)) {
        setContactSearchQuery("");
        return;
      }
      if (meta.action !== "input-change") return;
      setValue(fieldPaths.stop_contact_name, val);
      setContactSearchQuery(val);
      saveContext?.saveField(fieldPaths.stop_contact_name, val).catch(() => undefined);
    },
    [fieldPaths.stop_contact_name, saveContext, setValue],
  );

  return {
    fieldPaths,
    contactSearchResults: contactSelectOptions,
    isLoadingContacts,
    handleContactSelect,
    handleContactNameInputChange,
  };
}

export type StopContactSlice = ReturnType<typeof useStopContact>;
